import type { AxiosError } from 'axios'
import type { ChannelAuthorizationCallback } from 'pusher-js'
import * as Sentry from '@sentry/browser'
import Pusher from 'pusher-js'
import { UsersApi } from './apiClient'
import { getAxiosAuthorizationHeader } from './apiHelpers'
import { handleSkippedAPIError } from '~/apiErrorHandling'
import { isNoHttpResponse, retryOnNoResponse } from '~/noResponseRetry'
import { reportWarning } from '~/observability/warnings'

const PUSHER_AUTH_MAX_RETRIES = 4
const PUSHER_AUTH_RETRY_MS = 1000
// Every private channel authorizes on its own, so one dead connection fails
// about ten of them at once. One warning per burst is enough to see it.
const PUSHER_AUTH_REPORT_WINDOW_MS = 30_000
let lastPusherAuthReportAt = Number.NEGATIVE_INFINITY

export function resetPusherAuthReportThrottle() {
  lastPusherAuthReportAt = Number.NEGATIVE_INFINITY
}

async function getPusherAuthToken(
  usersAPI: UsersApi,
  channelName: string,
  socketId: string
): Promise<{ auth: string }> {
  const resp = await usersAPI.getTokenForPusherUsersTokenForPusherPost(
    channelName,
    socketId,
    { skipErrorHandling: true }
  )
  return { auth: resp.data.auth }
}

function shouldRetryPusherAuth(error: unknown): boolean {
  if (isNoHttpResponse(error)) {
    return true
  }
  const status = (error as AxiosError).response?.status
  return status != null && status >= 500 && status < 600
}

function reportPusherAuthFailure(error: unknown, channelName: string) {
  const err = error as AxiosError
  const status = err.response?.status ?? 0
  const online = typeof navigator === 'undefined' ? true : navigator.onLine
  if (!online && status === 0) {
    return
  }
  const now = Date.now()
  if (now - lastPusherAuthReportAt < PUSHER_AUTH_REPORT_WINDOW_MS) {
    return
  }
  lastPusherAuthReportAt = now
  reportWarning('Websocket auth request failed', {
    channelName,
    code: err.code,
    status,
    online,
  })
}

function socketMoved(client: Pusher, socketId: string): boolean {
  const current = client.connection.socket_id
  return Boolean(current) && current !== socketId
}

function socketMovedError(channelName: string): Error {
  return new Error(`Socket changed while authorizing ${channelName}`)
}

async function getPusherAuthTokenWithRetry(
  client: Pusher,
  channelName: string,
  socketId: string,
  callback: ChannelAuthorizationCallback
): Promise<void> {
  const usersAPI = new UsersApi()
  try {
    const auth = await retryOnNoResponse(
      () => {
        // The reconnect can land during the backoff, after shouldRetry already
        // said yes. Asking for a token for the old socket would be wasted at best.
        if (socketMoved(client, socketId)) {
          throw socketMovedError(channelName)
        }
        return getPusherAuthToken(usersAPI, channelName, socketId)
      },
      {
        extraAttempts: PUSHER_AUTH_MAX_RETRIES,
        delayMs: (attempt) => PUSHER_AUTH_RETRY_MS * 2 ** attempt,
        // A reconnect mints a new socket id. A token for the old one is useless.
        shouldRetry: (error) =>
          !socketMoved(client, socketId) && shouldRetryPusherAuth(error),
      }
    )
    // Same reason, other end: the answer can arrive after the reconnect, and
    // Pusher would send that token over the new connection.
    if (socketMoved(client, socketId)) {
      callback(socketMovedError(channelName), null)
      return
    }
    callback(null, auth)
  } catch (err) {
    // The skip above held back the 401 logout. Run it for a 401 only.
    if (
      (err as AxiosError).response?.status === 401 &&
      (await handleSkippedAPIError(err))
    ) {
      callback(
        new Error(`Failed to connect to channel ${channelName}: ${err}`),
        null
      )
      return
    }
    if (socketMoved(client, socketId)) {
      callback(socketMovedError(channelName), null)
      return
    }
    const errorMessage = `Failed to connect to channel ${channelName}: ${err}`
    console.error(errorMessage, err)
    reportPusherAuthFailure(err, channelName)
    callback(new Error(errorMessage), null)
  }
}

export function connectToSoketi(): Pusher {
  // https://www.npmjs.com/package/pusher-js
  const client = new Pusher('draydog', {
    wsHost: 'ws.draydog.com',
    wsPort: 443,
    wssPort: 443,
    cluster: 'not_used', // Not used but typing requires it
    forceTLS: true,
    // This needs to be both ws and wss of the connection will have state "failed." Per
    // the Pusher docs, they way to force TLS is to set forceTLS to true but still have
    // ws and wss in the enabledTransports array.
    enabledTransports: ['ws', 'wss'],
    disableStats: true, // These would be sent to pusher.com
    channelAuthorization: {
      transport: 'ajax',
      // We use customHandler instead, but type checking requires a value here
      endpoint: 'https://not_used',
      customHandler: (
        info: { channelName: string; socketId: string },
        callback: ChannelAuthorizationCallback
      ) => {
        const authHeader = getAxiosAuthorizationHeader()
        if (!authHeader) {
          console.error(
            `No auth header set when trying to connect to channel ${info.channelName}`
          )
          callback(new Error(`No auth header set`), null)
          return
        }
        // I was going to throw some caching in here, but it runs out that auth tokens
        // are tied to socketIDs so it doesn't really work to cache them
        getPusherAuthTokenWithRetry(
          client,
          info.channelName,
          info.socketId,
          callback
        )
      },
    },
  })

  // https://pusher.com/docs/channels/using_channels/connection/#available-states
  if (client.connection.state === 'failed') {
    Sentry.captureMessage('Pusher connection failed, possibly misconfigured')
  }
  // client.connection.bind_global((eventName, data) => {
  //   console.log('global Soketi connection event:', eventName, data)
  // })
  return client
}
